<script setup lang="ts">
import { computed, ref, watch } from "vue";
import { usePagination } from "@/hooks";
import {
	fetchWmsProductList,
	type FetchWmsProductListRequest,
} from "@/services/warehouseManage/products";
import type { FilterListItem } from "../CustomFilter/types";
import type { CustomTableColumnType, PaginationPosition } from "../CustomTable/types";

const emits = defineEmits<{
	confirm: [selectedRows: AObject[]];
}>();

const open = defineModel("open", {
	type: Boolean,
	default: false,
});

const searchData = ref<AObject>({});
const selectedRowKeys = ref<Key[]>([]);
const selectRows = ref<AObject[]>([]);

const filterList = ref<FilterListItem[]>([
	{
		label: "商品名称",
		field: "product_name",
		type: "input",
		placeholder: "请输入商品名称",
	},
]);

const columns: CustomTableColumnType<AObject>[] = [
	{
		title: "商品名称",
		dataIndex: "spu_name",
		key: "spu_name",
		width: 160,
	},
	{
		title: "商品图片",
		dataIndex: "theme_img",
		key: "theme_img",
		width: 140,
		customColumn: {
			type: "image",
			width: 80,
			height: 80,
		},
	},
	{
		title: "商品编号",
		dataIndex: "wms_spu_code",
		key: "wms_spu_code",
		width: 180,
	},
	{
		title: "规格型号",
		dataIndex: "specs",
		key: "specs",
		width: 160,
	},
	{
		title: "当前可用库存",
		dataIndex: "stock",
		key: "stock",
		width: 160,
	},
];

const getTableData = async (
	params: FetchWmsProductListRequest
): Promise<HttpResponse<ListDataResponse<AObject>>> => {
	const res = await fetchWmsProductList({ ...params });

	const list = res.data.list;

	const newData = list.reduce<AObject[]>((acc, item) => {
		const { sku_list, id, ...product } = item;

		const newItems = sku_list.map((sku) => ({
			...product,
			...sku,
			productId: id,
		})) as AObject[];

		return [...acc, ...newItems];
	}, []);

	return {
		...res,
		data: {
			...res.data,
			list: newData,
		},
	};
};

const { data, total, run, reset, loading, current, pageSize } = usePagination(getTableData, {
	defaultParams: { page: 1, page_size: 20 },
	manual: true,
});

const mergeColumnKeys = new Set(["spu_name", "theme_img", "wms_spu_code"]);

const rowSpanByIndex = computed(() => {
	const list = (data.value ?? []) as Array<{ spu_id?: number }>;
	const spans = new Array<number>(list.length).fill(1);

	let start = 0;

	while (start < list.length) {
		const currentSpuId = list[start]?.spu_id;
		let end = start + 1;

		while (end < list.length && list[end]?.spu_id === currentSpuId) {
			end += 1;
		}

		spans[start] = end - start;

		for (let idx = start + 1; idx < end; idx += 1) {
			spans[idx] = 0;
		}

		start = end;
	}

	return spans;
});

const mergedColumns = computed<CustomTableColumnType<AObject>[]>(() => {
	return columns.map((column) => {
		const columnKey = String(column.key ?? "");

		if (!mergeColumnKeys.has(columnKey)) {
			return column;
		}

		return {
			...column,
			customCell: (_record: AObject, index?: number) => {
				if (index === undefined) {
					return {};
				}

				return {
					rowSpan: rowSpanByIndex.value[index] ?? 1,
				};
			},
		};
	});
});

const pagination = computed(() => {
	return {
		current: current.value,
		pageSize: pageSize.value,
		total: total.value,
		position: "right" as PaginationPosition,
	};
});

watch(
	() => open.value,
	(value) => {
		if (value) {
			searchData.value = {};
			selectedRowKeys.value = [];
			selectRows.value = [];
			handleSearch();
		}
	}
);

const pageSizeChange = (current: number, pageSize: number) => {
	run({ page: current, page_size: pageSize });
};

const handleSearch = () => {
	reset({ ...searchData.value, page: 1, page_size: pageSize.value });
};

const onSelectChange = (keys: Key[], rows: AObject[]) => {
	selectedRowKeys.value = keys;
	selectRows.value = rows;
};

const rowSelection = computed(() => {
	return {
		selectedRowKeys: selectedRowKeys.value,
		onChange: onSelectChange,
	};
});

const handleConfirm = () => {
	open.value = false;

	emits("confirm", selectRows.value);
};
</script>

<template>
	<CustomDrawer v-model:open="open" title="选择商品" :width="1100" @ok="handleConfirm">
		<div class="full flex flex-col">
			<div class="pb-mm shrink-0 bg-white">
				<custom-filter
					v-model:value="searchData"
					:filter-list="filterList"
					:label-width="68"
					@reset="handleSearch"
					@search="handleSearch"
				/>
			</div>

			<custom-table
				row-key="sku_id"
				:loading="loading"
				:columns="mergedColumns"
				:data-source="data"
				:pagination="pagination"
				:show-expand-column="false"
				:row-selection="rowSelection"
				@page-size-change="pageSizeChange"
			></custom-table>
		</div>
	</CustomDrawer>
</template>
